/**
 * 参加者向けユーザーアプリの origin を「普遍的」に解決する (2026-05-30 ユーザー要望)。
 *
 * 管理画面で表示する各地区の参加 URL / QR は **ユーザーアプリの origin** であるべきだが、
 * 従来は `VITE_USER_APP_BASE_URL` env が未注入のとき `window.location.origin`(= admin 自身の
 * origin、ローカルなら :5174) に fallback して admin ドメインの URL になってしまっていた。
 *
 * 本 helper は env に依存せず、admin origin から規約変換で user origin を導出する:
 *   1. `VITE_USER_APP_BASE_URL` があれば最優先 (CI/deploy の明示注入)。
 *   2. 無ければ admin origin から導出:
 *      - ローカル admin vite (localhost/127.0.0.1 : 5174) → user vite ( : 5173)
 *      - hosting site 規約 `<name>-admin.<rest>` → `<name>-user.<rest>`
 *        (例: citizen-walk-stg-admin.web.app → citizen-walk-stg-user.web.app)
 *      - サブドメイン規約 `admin.<domain>` → `app.<domain>`
 *   3. どれにも当てはまらなければ admin origin をそのまま返す (従来 fallback)。
 */

/** pure: admin origin → user app origin (規約変換)。test 容易化のため分離。 */
export function deriveUserAppOrigin(adminOrigin: string): string {
  if (!adminOrigin) return adminOrigin;
  let u: URL;
  try {
    u = new URL(adminOrigin);
  } catch {
    return adminOrigin;
  }
  // ローカル: admin vite (5174) → user vite (5173)
  if ((u.hostname === 'localhost' || u.hostname === '127.0.0.1') && u.port === '5174') {
    u.port = '5173';
    return u.origin;
  }
  // hosting site 規約: <name>-admin(.|末尾) → <name>-user
  if (/-admin(\.|$)/.test(u.hostname)) {
    u.hostname = u.hostname.replace(/-admin(\.|$)/, '-user$1');
    return u.origin;
  }
  // サブドメイン規約: admin.<domain> → app.<domain>
  if (/^admin\./.test(u.hostname)) {
    u.hostname = u.hostname.replace(/^admin\./, 'app.');
    return u.origin;
  }
  return adminOrigin;
}

/** ユーザーアプリ origin を解決 (env 優先 → admin origin から導出)。 */
export function resolveUserAppOrigin(): string {
  const explicit = import.meta.env.VITE_USER_APP_BASE_URL as string | undefined;
  if (explicit && explicit.trim()) return explicit.trim();
  if (typeof window !== 'undefined' && window.location?.origin) {
    return deriveUserAppOrigin(window.location.origin);
  }
  return '';
}
